import { am, factory } from './utils';

import Vue from 'vue';
import httpClient from '@/utils/httpClient';
import { convertFiles } from '@/utils/filesConverter';
import { downloadInventionDocEntries } from '@/utils/inventionDocDownloader';

const EXEC = am(`EXEC_PROMPT`);
const CONTEXT = am(`CONTEXT`);
const INITIALIZE = am(`PROMPT_INITIALIZE`);
const GET_MODELS = am(`GET_MODELS`);
const GET_DOCUMENT_TYPES = am(`GET_DOCUMENT_TYPES`);
const GET_DOCUMENTATION = am(`GET_DOCUMENTATION`);
const HISTORY = {
  ...am(`HISTORY`),
  ACTIVATING: 'HISTORY_ACTIVATING',
  ACTIVATED: 'HISTORY_ACTIVATED'
};

const HISTORY_VERSION = am(`HISTORY_VERSION`);
const DRAFT = am(`DRAFT`);

const EXEC_PROGRESS = 'EXEC_PROGRESS';
const TOOL_CALL = 'TOOL_CALL';
const CONTENT = 'CONTENT';
const THINKING = 'THINKING';
const MESSAGE_ADDED = 'MESSAGE_ADDED';
const RETRY_OCCURED = 'RETRY_OCCURED';
const ERROR_OCCURED = 'ERROR_OCCURED';
const EXEC_ABORT = 'EXEC_ABORT';
const STREAM_START = 'STREAM_START';
const SEND_CHAT_MESSAGE = am('SEND_CHAT_MESSAGE');
const CLEAR_STATE = 'CLEARE_STATE';
const FORM_RESOLVING_DONE = 'FORM_RESOLVING_DONE';
const SKILL_LOADED = 'SKILL_LOADED';

function buildRequestPayload(state) {
  const payload = { ...state.lastSentPayload };

  return payload;
}

function messageReceived(commit, event, data, isChat) {
  switch (event) {
    case 'chain-start':
    case 'chat-start':
    case 'link-finish':
    case 'ping': {
      break;
    }
    case 'stream-start': {
      commit(STREAM_START, data);
      break;
    }
    case 'link-start': {
      commit(MESSAGE_ADDED, data);
      break;
    }
    case 'tool': {
      commit(TOOL_CALL, { event, data });
      break;
    }
    case 'content': {
      commit(CONTENT, { event, data });
      break;
    }
    case 'stream': {
      if (data?.content === undefined) {
        throw new Error(`Bad stream data: ${data ? JSON.stringify(data) : undefined}`);
      }

      commit(EXEC_PROGRESS, data?.content);
      break;
    }
    case 'thinking': {
      commit(THINKING, { event, data });
      break;
    }
    case 'chat-end': {
      commit(SEND_CHAT_MESSAGE.COMPLETED);
      break;
    }
    case 'chain-finish': {
      commit(EXEC.COMPLETED, { event, data });
      break;
    }
    case 'retry': {
      commit(RETRY_OCCURED, data);
      break;
    }
    case 'skill-loaded': {
      commit(SKILL_LOADED, data);
      break;
    }
    case 'error': {
      if (isChat) {
        commit(SEND_CHAT_MESSAGE.FAILED, data);
      } else {
        commit(ERROR_OCCURED, data);
      }
      break;
    }
  }
}

/**
 * Resolves form variables that have a source.docCode by finding matching invention documents
 * and pre-populating the variable value — mirrors AlfredModel.ResolveFormVariables in the word add-in.
 */
async function resolveDocCodeVariables(variables, inventionDocs, signal) {
  const tasks = variables.map(async variable => {
    const docCode = variable.source?.docCode?.trim();
    if (!docCode || docCode === ' ' || docCode === 'CURRENT_DOCUMENT') return;

    const selector = variable.source?.selector || 'all';
    const isImageType = variable.type === 'image';

    let matchingDocs = (inventionDocs || []).filter(doc => Array.isArray(doc.tags) && doc.tags.some(t => t.toLowerCase() === docCode.toLowerCase()));

    if (!matchingDocs.length) return;

    if (selector === 'newest') {
      matchingDocs = [...matchingDocs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 1);
    } else if (selector === 'oldest') {
      matchingDocs = [...matchingDocs].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).slice(0, 1);
    }

    try {
      const results = await Promise.allSettled(
        matchingDocs.map(doc => downloadInventionDocEntries(doc, { fileType: isImageType ? 'image' : 'text', signal }))
      );

      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

      const allEntries = results.filter(r => r.status === 'fulfilled').flatMap(r => r.value);

      if (!allEntries.length) return;

      if (isImageType) {
        const imageEntries = allEntries.filter(e => e.type === 'base64' || (e.mimeType || '').startsWith('image/'));
        if (imageEntries.length) {
          const existing = Array.isArray(variable.value) ? variable.value : [];
          variable.value = [...existing, ...imageEntries];
        }
      } else {
        const textEntries = allEntries.filter(e => e.type === 'text' && e.content);
        if (textEntries.length) {
          variable.sourceFiles = [...(variable.sourceFiles || []), ...textEntries];
          variable.value = '';
        }
      }
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      // skip other errors
    }
  });

  await Promise.all(tasks);
}

async function execute({ commit, state, getters, rootState }) {
  const chain = state.contentChain?.template?.chain;
  if (!chain?.length) {
    return;
  }

  try {
    while (state.cursor && !state.cancelled) {
      const begin = state.cursor;
      const nextForm = chain.slice(chain.indexOf(begin) + 1).find(link => link.type === 'form' && link.breakpoint);
      const end = nextForm ? chain[chain.indexOf(nextForm) - 1] : chain[chain.length - 1];

      let form;

      if (begin.type === 'form') {
        form = begin;
      }

      if (form && !state.continue) {
        // current step is form and it is not solved
        const resolvedVars = (
          await httpClient.post(
            `/api/v2/nlp/chains/variables/resolve`,
            {
              variables: [...getters.previousRunValues.filter(v => !form.variables.find(fv => fv.name === v.name)), ...form.variables],
              context: state.context
            },
            undefined,
            { signal: state.cancellation?.signal }
          )
        )
          .filter(v => form.variables.find(fv => fv.name === v.name))
          .map(v => ({ ...v, sourceFiles: [], origin: 'form' }));

        // Detect whether any variables need doc-code resolution (file downloads)
        const hasDocCodes = resolvedVars.some(v => {
          const code = v.source?.docCode?.trim();
          return code && code !== ' ' && code !== 'CURRENT_DOCUMENT';
        });

        form.isResolvingDocCodes = hasDocCodes;
        form.variables = resolvedVars;
        // Show the form immediately so the user sees it while files are loading
        commit(MESSAGE_ADDED, form);

        if (hasDocCodes) {
          // Resolve in the background — variables are mutated in-place so Vue reactivity picks up changes
          await resolveDocCodeVariables(resolvedVars, rootState.inventionFiles?.files, state.cancellation?.signal);
          if (!state.cancelled) {
            commit(FORM_RESOLVING_DONE, form.name);
          }
        }

        break;
      } else {
        if (form && begin === end) {
          // single form case
          state.cursor = null;
          commit(EXEC.COMPLETED);
          break;
        } else {
          // current step is not a form or solved form so we execute chain to the next form or to the end
          state.continue = false;
          state.cursor = nextForm;

          const body = {
            ...state.contentChain,
            template: {
              begin: begin.breakpoint,
              end: end.breakpoint,
              ...state.contentChain.template,
              variables: getters.previousRunValues
            },
            debug: true
          };

          await httpClient.stream(`/api/v2/nlp/chains/stream`, { body, signal: state.cancellation.signal }, (event, data) =>
            messageReceived(commit, event, data, false)
          );
        }
      }
    }
  } catch (e) {
    if (e?.name === 'AbortError') return;
    try {
      const responseText = await e.response.text();
      const { message } = JSON.parse(responseText);
      commit(EXEC.FAILED, message);
    } catch (e) {
      commit(EXEC.FAILED, 'Unable to generate an output.');
    }
  }
}

const USER_AUTHORS = ['CURRENT_USER', 'template-generator', 'hub-vectorizer'];

/**
 * Ensures a supplemental file object has a unique id.
 * Files sourced from the DB already carry their id; files created locally (disk uploads,
 * inline images) get a generated UUID so the backend can reference them in metadata.
 */
function ensureId(file) {
  return file.id ? file : { ...file, id: crypto.randomUUID() };
}

function formToHistoryEntry(response) {
  const textParts = [response.name].filter(Boolean);
  const supplementalFiles = [];

  for (const variable of response.variables) {
    if (variable.type === 'image' && Array.isArray(variable.value) && variable.value.length) {
      for (const value of variable.value) {
        supplementalFiles.push(ensureId(value));
      }
    } else if (Array.isArray(variable.sourceFiles) && variable.sourceFiles.length) {
      supplementalFiles.push(...variable.sourceFiles.map(ensureId));
    } else if (variable.value !== undefined && variable.value !== '') {
      textParts.push(`${variable.name}: ${variable.value}`);
    }
  }

  return {
    role: 'user',
    content: textParts.join('\n'),
    supplementals: { files: supplementalFiles }
  };
}

function messageToHistoryEntry(response) {
  const isUser = USER_AUTHORS.includes(response.author);
  // Include all attached files: images carry inline content, non-images carry their id for backend lookup
  const attachedFiles = isUser ? (response.attachedFileNames ?? []).map(ensureId) : [];

  return {
    role: isUser ? 'user' : 'assistant',
    content: response.text,
    supplementals: { files: [...(response.files ?? []), ...attachedFiles].map(ensureId) }
  };
}

export default {
  namespaced: true,
  // leave this here until further features are added
  ...factory({
    state: {
      history: {
        isRequestPending: false,
        isRequestFailed: false,
        entries: []
      },
      preview: null,
      draft: null,
      current: null,
      currentView: undefined,
      currentViewKey: undefined,
      isDraftRequestPending: false,
      isDraftRequestFailed: false,

      isLoadingDocumentTypes: false,
      isLoadingDocumentTypesFailed: false,
      documentTypes: {}
    },
    mutations: {
      [HISTORY.STARTED](state) {
        state.history.isRequestPending = true;
        state.history.isRequestFailed = false;
        state.history.entries = [];
        state.history.error = null;
      },
      [HISTORY.COMPLETED](state, entries) {
        state.history.isRequestPending = false;
        state.history.isRequestFailed = false;

        if (state.history.entries.length === 0) {
          state.history.entries = entries;
        } else {
          const newEntries = entries.filter(ne => !state.history.entries.find(he => he.id === ne.id));
          state.history.entries.unshift(...newEntries);
        }

        state.history.entries.forEach(he => (he.isActive = false));
        const activeVersion = state.item?.version;

        if (activeVersion) {
          const activeEntry = state.history.entries.find(he => he.version === activeVersion);
          if (activeEntry) {
            activeEntry.isActive = true;
          } else {
            state.history.entries[0].isActive = true;
          }
        } else {
          state.history.entries[0].isActive = true;
        }
      },
      [HISTORY.ACTIVATING](state) {
        state.history.isRequestPending = true;
        state.history.isRequestFailed = false;
        state.currentViewKey = undefined;
      },
      [HISTORY.ACTIVATED](state, entry) {
        state.history.isRequestPending = false;
        state.history.isRequestFailed = false;
        const toActivate = state.history.entries.find(he => he.id === entry.id);
        if (toActivate) {
          state.history.entries.forEach(he => (he.isActive = false));
          toActivate.isActive = true;
        }
      },
      [HISTORY.FAILED](state, error) {
        state.history.isRequestPending = false;
        state.history.isRequestFailed = true;
        state.history.entries = [];
        state.history.error = error;
      },
      [CLEAR_STATE](state) {
        state.item = null;
        state.history = {
          isRequestPending: false,
          isRequestFailed: false,
          entries: []
        };
        state.preview = null;
        state.draft = null;
        state.item = null;
        state.currentView = undefined;
        state.currentViewKey = undefined;
      },
      [HISTORY_VERSION.STARTED](state) {
        state.isGetRequestPending = true;
        state.isGetRequestFailed = false;
      },
      [HISTORY_VERSION.COMPLETED](state, item) {
        state.isGetRequestPending = false;
        state.isGetRequestFailed = false;
        state.preview = item;
      },
      [HISTORY_VERSION.FAILED](state) {
        state.isGetRequestPending = false;
        state.isGetRequestFailed = true;
        state.preview = null;
      },
      [DRAFT.STARTED](state) {
        state.isDraftRequestPending = true;
        state.isDraftRequestFailed = false;
      },
      [DRAFT.COMPLETED](state, item) {
        state.isDraftRequestPending = false;
        state.isDraftRequestFailed = false;
        state.draft = item;
      },
      [DRAFT.FAILED](state) {
        state.isDraftRequestPending = false;
        state.isDraftRequestFailed = true;
        state.draft = null;
      },
      ['VIEW_STATE'](state, view) {
        state.current = null;
        state.currentView = null;

        switch (view) {
          case 'draft':
            state.preview = null;
            if (state.draft?.data) {
              state.current = state.draft?.data;
              state.currentViewKey = state.draft?.id;
              state.currentView = view;
            } else {
              state.current = state.item;
              state.currentViewKey = state.item?.id;
              state.currentView = 'item';
            }
            break;
          case 'item':
            state.preview = null;
            state.current = state.item;
            state.currentViewKey = state.item?.id;
            state.currentView = view;
            break;
          case 'preview':
            if (state.preview?.data) {
              state.current = state.preview?.data;
              state.currentViewKey = state.preview?.id;
              state.currentView = view;
            } else {
              state.preview = null;
              state.current = state.item;
              state.currentViewKey = state.item?.id;
              state.currentView = 'item';
            }
            break;
        }
      },
      [GET_DOCUMENT_TYPES.STARTED](state) {
        state.isLoadingDocumentTypes = true;
        state.isLoadingDocumentTypesFailed = false;
      },
      [GET_DOCUMENT_TYPES.COMPLETED](state, documentTypes) {
        state.isLoadingDocumentTypes = false;
        state.documentTypes = documentTypes;
      },
      [GET_DOCUMENT_TYPES.FAILED](state) {
        state.isLoadingDocumentTypes = false;
        state.isLoadingDocumentTypesFailed = true;
      }
    },
    actions: {
      async getDocumentTypes({ commit }) {
        try {
          commit(GET_DOCUMENT_TYPES.STARTED);
          const documentTypes = await httpClient.get(`/api/filesmanager/files/types`);
          commit(GET_DOCUMENT_TYPES.COMPLETED, documentTypes);
        } catch (error) {
          commit(GET_DOCUMENT_TYPES.FAILED);
        }
      },
      async fetchHistory({ commit }, chainId) {
        try {
          commit(HISTORY.STARTED);
          const entires = await httpClient.get(`/api/prompts/${chainId}/history`);
          commit(HISTORY.COMPLETED, entires);
        } catch (error) {
          commit(HISTORY.FAILED, error);
        }
      },
      async activateHistoryEntry({ commit }, entry) {
        try {
          commit(HISTORY.ACTIVATING);
          const active = await httpClient.post(`/api/prompts/${entry.libraryItemId}/history/activate`, {
            version: entry
          });
          commit(HISTORY.ACTIVATED, active);
        } catch (error) {
          commit(HISTORY.FAILED, error);
          throw error;
        } finally {
          await this.dispatch('prompts/getById', entry.libraryItemId);
          await this.dispatch('prompts/switchView', 'item');
        }
      },
      async viewHistoryEntry({ commit }, entry) {
        try {
          commit(HISTORY_VERSION.STARTED);
          const historyVersion = await this.dispatch('prompts/getHistoryEntry', entry);
          commit(HISTORY_VERSION.COMPLETED, { ...historyVersion });
          await this.dispatch('prompts/switchView', 'preview');
        } catch (error) {
          commit(HISTORY_VERSION.FAILED);
          throw error;
        }
      },
      async deleteHistoryEntry({ commit }, entry) {
        try {
          commit(HISTORY_VERSION.STARTED);
          await httpClient.delete(`/api/prompts/${entry.libraryItemId}/history/${entry.id}`);
          commit(HISTORY_VERSION.COMPLETED, null);
          await this.dispatch('prompts/fetchHistory', entry.libraryItemId);
        } catch (error) {
          commit(HISTORY_VERSION.FAILED);
          throw error;
        }
      },
      async editHistory({ commit }, entry) {
        try {
          commit(HISTORY_VERSION.STARTED);
          await httpClient.patch(`/api/prompts/${entry.libraryItemId}/history/${entry.id}`, { ...entry });
          commit(HISTORY_VERSION.COMPLETED, null);
          await this.dispatch('prompts/fetchHistory', entry.libraryItemId);
        } catch (error) {
          commit(HISTORY_VERSION.FAILED);
          throw error;
        }
      },
      async getHistoryEntry({}, entry) {
        return httpClient.get(`/api/prompts/${entry.libraryItemId}/history/${entry.id}`);
      },
      async saveDraft({ commit }, { id, item }) {
        try {
          commit(DRAFT.STARTED);
          const draft = await httpClient.patch(`/api/prompts/${id}/draft`, { ...item });
          commit(DRAFT.COMPLETED, draft);
          await this.dispatch('prompts/switchView', 'draft');
        } catch (error) {
          commit(DRAFT.FAILED);
          throw error;
        }
      },
      async saveVersion({}, { id, item }) {
        await this.dispatch('prompts/update', {
          ...item,
          id
        });
        await Promise.all([this.dispatch('prompts/getById', id), this.dispatch('prompts/fetchHistory', id)]);
        await this.dispatch('prompts/switchView', 'item');
      },
      async loadDraft({ commit }, id) {
        try {
          commit(DRAFT.STARTED);
          const draft = await httpClient.get(`/api/prompts/${id}/draft`);
          commit(DRAFT.COMPLETED, draft);
        } catch (error) {
          commit(DRAFT.FAILED);
          throw error;
        }
      },
      async activateDraft({}, { id, comment }) {
        try {
          await httpClient.post(`/api/prompts/${id}/draft/activate`, { comment });
        } catch (error) {
          throw error;
        } finally {
          await Promise.all([this.dispatch('prompts/getById', id), this.dispatch('prompts/loadDraft', id)]);
          await this.dispatch('prompts/switchView', 'item');
        }
      },
      async deleteDraft({ commit }, id) {
        try {
          commit(DRAFT.STARTED);
          await httpClient.delete(`/api/prompts/${id}/draft`);
          commit(DRAFT.COMPLETED, null);
          await this.dispatch('prompts/switchView', 'item');
        } catch (error) {
          commit(DRAFT.FAILED);
          throw error;
        }
      },
      clear({ commit }) {
        commit(CLEAR_STATE);
      },
      switchView({ commit }, view) {
        commit('VIEW_STATE', view);
      },
      async load({ state }, id) {
        await Promise.all([this.dispatch('prompts/getById', id), this.dispatch('prompts/loadDraft', id), this.dispatch('prompts/fetchHistory', id)]);
        await this.dispatch('prompts/switchView', state.draft ? 'draft' : 'item');
      },
      validateTemplate({}, template) {
        return httpClient.post(`/api/prompts/template/validate`, { template });
      }
    },
    getters: {
      isBusy(state) {
        return (
          state.isGetRequestPending ||
          state.isPostRequestPending ||
          state.isPutRequestPending ||
          state.isDeleteRequestPending ||
          state.sample.isRequestPending
        );
      }
    }
  })(
    'prompt',
    {
      async getById(id) {
        return httpClient.get(`/api/prompts/${id}`);
      },
      async getCollection() {
        return httpClient.get('/api/prompts/');
      },
      async create(payload) {
        const { serves, params, ...cleanPayload } = payload;
        return httpClient.post('/api/prompts/', cleanPayload);
      },
      async update(id, payload) {
        const { serves, params, ...cleanPayload } = payload;
        return httpClient.patch(`/api/prompts/${id}`, cleanPayload);
      },
      async delete(id) {
        return httpClient.delete(`/api/prompts/${id}`);
      }
    },
    {
      sample: {
        namespaced: true,
        state: {
          isRequestPending: false,
          isRequestFailed: false,
          response: [],
          isLoadingModels: false,
          models: [],
          contentChain: null,
          cancellation: null,
          previousRun: { output: [], forms: [] },
          sessionContext: {
            invention: null,
            document: null,
            publicData: false
          },
          context: {
            invention: null,
            document: null,
            publicData: false
          },
          lastSentPayload: null,
          skillToasts: [],
          learnedSkills: []
        },
        getters: {
          previousRunValues(state) {
            return [...state.previousRun.output, ...state.previousRun.forms];
          }
        },
        mutations: {
          [INITIALIZE.STARTED](state, chain) {
            state.isRequestPending = false;
            state.isRequestFailed = false;
            state.contentChain = null;
            state.cancellation = null;
            state.response = [];
            state.learnedSkills = [];

            chain = chain ? JSON.parse(JSON.stringify(chain)) : [];

            const links = chain.filter(l => l.type !== 'form');
            const forms = chain.filter(l => l.type === 'form');

            const formVariables = [];

            forms.forEach(f => {
              formVariables.push(
                ...f.variables.map(v => ({
                  form: f.name,
                  ...v
                }))
              );
            });

            state.previousRun = {
              output: links.map(link => ({
                link: link.type,
                linkName: link.name,
                name: `${link.type.toUpperCase()}_${links.filter(l => l.type === link.type).indexOf(link) + 1}_OUTPUT`,
                value: ''
              })),
              forms: formVariables
            };
          },
          [EXEC.STARTED](state, chainToExecute) {
            if (state.cancellation) {
              state.cancellation.abort();
            }

            const contentChain = JSON.parse(JSON.stringify(chainToExecute));
            const chain = contentChain.template.chain;
            chain.forEach((link, index) => {
              link.breakpoint = `${link.name}_${link.type}_${index}`;
            });

            state.cursor = chain[0];
            state.execOne = chainToExecute.execOne || false;
            state.index = chainToExecute.index || 0;
            state.contentChain = contentChain;
            state.context = contentChain.context;
            state.cancellation = new AbortController();
            state.continue = false;
            state.cancelled = false;
            state.isRequestPending = true;
            state.isRequestFailed = false;
            state.response = [];
          },
          [EXEC_PROGRESS](state, response) {
            var message = state.response[state.response.length - 1];

            if (!message) {
              return;
            }

            message.text += response;
          },
          [TOOL_CALL](state, { event, data }) {
            const tool = data.tool;
            if (tool?.result) {
              state.response.splice(state.response.length - 1, 0, {
                ...tool,
                text: tool.content,
                key: tool.callId,
                type: event,
                author: tool.name,
                expanded: false
              });
            }
          },
          [CONTENT](state, { event, data }) {
            const file = data.data;
            if (file) {
              const contentResponse = state.response[state.response.length - 1];
              if (!contentResponse.files?.length) {
                contentResponse.files = [];
              }
              contentResponse.files.push(file);
            }
          },
          [THINKING](state, { event, data }) {
            // if model streaming thinking process
            if (data.streaming) {
              const last = state.response[state.response.length - 2];

              if (last?.type === event) {
                last.text += data.content;
                return;
              }
            }

            // if model stream separate thoughts
            state.response.splice(state.response.length - 1, 0, {
              text: data.content,
              type: event,
              author: data.topic ?? 'Thinking',
              expanded: false
            });
          },
          [STREAM_START](state, event) {
            const userMessages = state.response
              .slice()
              .reverse()
              .find(m => m.author === 'CURRENT_USER');

            if (userMessages) {
              userMessages.request = event.request;
            }
          },
          [MESSAGE_ADDED](state, link) {
            let responseMessage;
            let authorMessage;
            switch (link.type) {
              case 'chat':
              case 'dumb':
              case 'prompt':
                {
                  const messages = link.messages || link.link?.messages || (link.chain && link.chain[0]?.messages) || [];
                  const modelId = link.model || link.link?.model || (link.chain && link.chain[0]?.model);
                  const author = this.getters['nlp/getModelById'](modelId)?.name || modelId;
                  const userMessage = messages.filter(m => m.role === 'user').slice(-1)[0] ?? messages.find(m => m.role === 'user');
                  const systemMessage = messages.filter(m => m.role === 'assistant').slice(-1)[0] ?? messages.find(m => m.role === 'system');
                  authorMessage = {
                    text: userMessage?.content || userMessage,
                    author: 'CURRENT_USER',
                    templateDataExpanded: false,
                    attachedFileNames: userMessage?.attachedFileNames ?? []
                  };
                  responseMessage = {
                    text: '',
                    name: link.name,
                    type: link.type,
                    author: author,
                    system: systemMessage.content,
                    retry: false,
                    error: false
                  };
                }
                break;
              case 'function':
                {
                  const author = link.model;
                  authorMessage = {
                    text: `Execute query '${link.name?.toUpperCase()}'`,
                    author: 'CURRENT_USER'
                  };
                  responseMessage = {
                    text: '',
                    name: link.name,
                    type: link.type,
                    author
                  };
                }
                break;
              case 'query':
                {
                  const author = link.model;
                  authorMessage = {
                    text: `Execute query '${link.name?.toUpperCase()}'`,
                    author: 'CURRENT_USER'
                  };
                  responseMessage = {
                    text: '',
                    name: link.name,
                    type: link.type,
                    author
                  };
                }
                break;
              case 'template': {
                const author = link.model;
                authorMessage = {
                  text: `Execute template '${link.name?.toUpperCase()}'`,
                  author: 'CURRENT_USER'
                };
                responseMessage = {
                  text: '',
                  name: link.name,
                  type: link.type,
                  author
                };
                break;
              }
              case 'tool': {
                authorMessage = {
                  text: `Execute tool '${(link.tool || link.name)?.toUpperCase()}'`,
                  author: 'CURRENT_USER'
                };
                responseMessage = {
                  text: '',
                  name: link.name,
                  type: link.type,
                  author: link.tool || link.name || 'tool'
                };
                break;
              }
              case 'form':
                {
                  authorMessage = {
                    ...link,
                    done: false
                  };
                }
                break;
              case 'content': {
                const author = link.model;
                authorMessage = {
                  text: `Prepare content(s) '${link.name?.toUpperCase()}'`,
                  author: 'CURRENT_USER'
                };
                responseMessage = {
                  value: [],
                  name: link.name,
                  type: link.type,
                  author
                };
                break;
              }
            }
            state.response.push({ key: Math.floor(Math.random() * 1000000), ...authorMessage });
            if (responseMessage) {
              state.response.push({ key: Math.floor(Math.random() * 1000000), ...responseMessage });
            }
          },
          [RETRY_OCCURED](state, event) {
            state.response[state.response.length - 1].retry = true;
            state.response[state.response.length - 1].text = 'An error occured. Retrying...';

            if (event.type === 'prompt') {
              const systemMessage = event.messages.find(m => m.role === 'system');
              state.response.push({
                text: '',
                author: event.model,
                system: systemMessage.content,
                retry: false,
                error: false
              });
            } else {
              state.response.push({
                text: '',
                author: event.model,
                system: 'Failed to execute query',
                retry: false,
                error: false
              });
            }
          },
          [ERROR_OCCURED](state, event) {
            let prompt = state.response.filter(t => t.type !== 'form')[state.response.length - 1];
            if (!prompt) {
              prompt = {
                type: 'prompt',
                name: 'Error',
                author: 'Content Chain',
                text: `${event.content}`,
                error: true
              };
              state.response.push(prompt);
            } else {
              prompt.text = event.content;
              prompt.error = true;
            }
          },
          [EXEC.COMPLETED](state, { event, data }) {
            const modelResponses = state.response.filter(m => m.author !== 'CURRENT_USER');

            // executing one link from a chain
            if (state.execOne) {
              const chain = state.contentChain.template.chain;
              const running = chain[0];
              if (running.link !== 'form' && running.type !== 'form') {
                const output = state.previousRun.output[state.index];
                if (output) {
                  output.value = modelResponses[0].text;
                } else {
                  state.previousRun.output.push({
                    linkType: running.type,
                    linkName: running.name,
                    name: `${running.type.toUpperCase()}_${chain.filter(l => l.type === running.type).indexOf(running) + 1}_OUTPUT`,
                    value: '',
                    origin: 'chain'
                  });
                }
              } else {
                for (const variable of running.variables) {
                  const form = state.previousRun.forms.find(fv => fv.name === variable.name);
                  if (form) {
                    form.value = variable.value;
                  } else {
                    state.previousRun.forms.push({
                      linkType: 'form',
                      linkName: running.name,
                      name: variable.name,
                      value: variable.value,
                      origin: 'form'
                    });
                  }
                }
              }
            } else {
              // running the whole chain
              modelResponses
                .filter(r => r.type != 'form')
                .map(response => ({
                  linkType: response.type,
                  linkName: response.name,
                  name: `${response.type.toUpperCase()}_${modelResponses.filter(l => l.type === response.type).indexOf(response) + 1}_OUTPUT`,
                  value: response.text
                }))
                .forEach(newValue => {
                  const existingValue = state.previousRun.output.find(o => o.name === newValue.name);
                  if (existingValue) {
                    existingValue.value = newValue.value;
                  } else {
                    state.previousRun.output.push(newValue);
                  }
                });

              const { variables } = data;

              variables
                .filter(r => r.origin === 'form')
                .forEach(newValue => {
                  const existingValue = state.previousRun.forms.find(o => o.name === newValue.name);
                  if (existingValue) {
                    existingValue.value = newValue.value;
                    existingValue.origin = 'form';
                  } else {
                    state.previousRun.forms.push(newValue);
                  }
                });
            }

            // current run is not finished
            if (state.cursor) {
              return;
            }

            // current run is finished
            state.isRequestPending = false;
            state.cancellation?.abort();
          },
          [EXEC_ABORT](state) {
            state.isRequestPending = false;
            state.cancellation?.abort();
            state.cancelled = true;
            state.response.forEach(msg => {
              if (msg.type === 'form' && msg.isResolvingDocCodes) {
                Vue.set(msg, 'isResolvingDocCodes', false);
              }
            });
          },
          [FORM_RESOLVING_DONE](state, formName) {
            const msg = state.response.find(m => m.type === 'form' && m.name === formName);
            if (msg) Vue.set(msg, 'isResolvingDocCodes', false);
          },
          REMOVE_LEARNED_SKILL(state, name) {
            const idx = state.learnedSkills.indexOf(name);
            if (idx !== -1) state.learnedSkills.splice(idx, 1);
          },
          [SKILL_LOADED](state, data) {
            const id = Date.now();
            state.skillToasts.push({ id, displayDescription: data.displayDescription });
            setTimeout(() => {
              const idx = state.skillToasts.findIndex(t => t.id === id);
              if (idx !== -1) state.skillToasts.splice(idx, 1);
            }, 4000);
            if (!state.learnedSkills.includes(data.name)) {
              state.learnedSkills.push(data.name);
            }
          },
          [EXEC.FAILED](state) {
            state.isRequestPending = false;
            state.isRequestFailed = true;
            state.response = [];
            state.cancellation?.abort();
          },
          [CONTEXT.STARTED](state) {
            state.isRequestPending = true;
            state.isRequestFailed = false;
          },
          [CONTEXT.COMPLETED](state, context) {
            state.isRequestPending = false;
            state.context = context;
          },
          [CONTEXT.FAILED](state, context) {
            state.isRequestPending = false;
            state.isRequestFailed = true;
            state.context = context;
          },
          [GET_MODELS.STARTED](state) {
            state.isLoadingModels = true;
          },
          [GET_MODELS.COMPLETED](state, models) {
            state.isLoadingModels = false;
            state.models = models;
          },
          [GET_MODELS.FAILED](state) {
            state.isLoadingModels = false;
          },
          [GET_DOCUMENTATION.STARTED](state) {
            state.isGetHelpPending = true;
            state.isGetHelpFailed = false;
          },
          [GET_DOCUMENTATION.COMPLETED](state, documentation) {
            state.isGetHelpPending = false;
            state.documentation = documentation;
          },
          [GET_DOCUMENTATION.FAILED](state) {
            state.isGetHelpFailed = true;
            state.isGetHelpPending = false;
          },
          [SEND_CHAT_MESSAGE.STARTED](state) {
            state.isRequestPending = true;
            state.isRequestFailed = false;
            state.cancellation = new AbortController();
          },
          [SEND_CHAT_MESSAGE.COMPLETED](state) {
            state.isRequestPending = false;
            state.cancellation?.abort();
          },
          [SEND_CHAT_MESSAGE.FAILED](state, error) {
            state.isRequestPending = false;
            state.isRequestFailed = true;
            state.cancellation?.abort();

            var message = state.response[state.response.length - 1];

            if (!message) {
              return;
            }

            message.text += error.content;
          }
        },
        actions: {
          async getChat({}, invention) {
            return httpClient.get(`/api/v2/nlp/chat/template/${invention?.id ?? 'unset'}`);
          },
          async exec({ commit, state, getters, rootState }, chainTemplate) {
            commit(EXEC.STARTED, chainTemplate);
            await execute({ commit, state, getters, rootState });
          },
          async continue({ commit, state, getters, rootState }) {
            if (state.cursor?.type === 'form' && Array.isArray(state.cursor.variables)) {
              for (const variable of state.cursor.variables) {
                const sourceFiles = variable.sourceFiles || [];

                if ((variable.type === 'text' || variable.type === 'string') && !variable.value && sourceFiles.length) {
                  variable.value = sourceFiles.map(sf => sf.content || '').filter(Boolean).join('\n\n');
                }

                const existing = state.previousRun.forms.find(f => f.name === variable.name);
                if (existing) {
                  existing.value = variable.value;
                  existing.sourceFiles = sourceFiles;
                } else {
                  state.previousRun.forms.push({ form: state.cursor.name, ...variable });
                }
              }
            }

            state.continue = true;
            await execute({ commit, state, getters, rootState });
          },
          async generateContext({ commit }, { document }) {
            try {
              commit(CONTEXT.STARTED);
              const context = await httpClient.post(`/api/nlp/api/v1/parser/parse`, {
                document,
                sections: ['claims']
              });

              commit(CONTEXT.COMPLETED, context);
            } catch (e) {
              commit(CONTEXT.FAILED);
              throw e;
            }
          },
          reset({ commit }, chain) {
            commit(INITIALIZE.STARTED, chain);
          },
          async closeStream({ commit }) {
            commit(EXEC_ABORT);
          },
          async getDocumentation({ commit }) {
            try {
              commit(GET_DOCUMENTATION.STARTED);
              const response = await httpClient.get(`/api/nlp/api/v1/generator/documentation`);
              commit(GET_DOCUMENTATION.COMPLETED, response);
            } catch (e) {
              commit(GET_DOCUMENTATION.FAILED);
              throw e;
            }
          },
          async sendChatMessage({ commit, state }, chatPayload) {
            commit(SEND_CHAT_MESSAGE.STARTED);

            const attachedFiles = chatPayload.attachedFiles ?? [];
            delete chatPayload.attachedFiles;

            // Merge skills learned in this session so the backend treats them as already active
            if (state.learnedSkills?.length) {
              const chat = chatPayload.template?.chat;
              if (chat) {
                const existing = chat.activeSkills ?? [];
                const merged = [...new Set([...existing, ...state.learnedSkills])];
                chat.activeSkills = merged;
              }
            }

            // Build conversation history from prior responses
            const history = [];
            for (const response of state.response ?? []) {
              if (response.type === 'form' && response.variables?.length) {
                history.push(formToHistoryEntry(response));
              } else if (response.type === 'tool') {
                if (response.cache) {
                  history.push({ role: 'function', name: response.author, content: response.text });
                }
                // uncached tool results are not included in the conversation history
              } else {
                history.push(messageToHistoryEntry(response));
              }
            }

            // Tag the user message with attached file names for display in the UI
            if (attachedFiles.length) {
              const userMsgInChat = chatPayload.template.chat.messages.find(m => m.role === 'user');
              if (userMsgInChat) {
                userMsgInChat.attachedFileNames = attachedFiles.map(f => ({ ...f, mimeType: f.mimeType || '' }));
              }
            }

            commit(MESSAGE_ADDED, chatPayload.template.chat);

            const systemMessage = chatPayload.template.chat.messages.find(m => m.role === 'system');
            let userMessage = chatPayload.template.chat.messages.find(m => m.role === 'user');

            if (attachedFiles.length) {
              userMessage = {
                role: 'user',
                content: userMessage.content,
                supplementals: { files: attachedFiles.map(ensureId) }
              };
            }

            chatPayload.template.chat.messages = [systemMessage, ...history, userMessage];

            try {
              await httpClient.stream('/api/v2/nlp/chat', { body: chatPayload, signal: state.cancellation.signal }, (event, data) =>
                messageReceived(commit, event, data, true)
              );
            } catch (error) {
              if (error?.name === 'AbortError') return;
              commit(SEND_CHAT_MESSAGE.FAILED, error);
              throw error;
            }
          }
        }
      }
    }
  )
};
